import { AuthProvider, OAuthActionType, OAuthSuccessResponse } from '@avanzu/types';
import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { apiClient } from '../../../services';
import ProviderCard from './ProviderCard';

interface GoogleAccountLinkProps {
  onSuccess: (message: string, provider: AuthProvider) => void;
  onError: (error: string, provider: AuthProvider) => void;
  onClearError: () => void;
}

const GoogleAccountLink: React.FC<GoogleAccountLinkProps> = ({
  onError,
  onClearError,
  onSuccess,
}) => {
  const { user, refreshUserOrganisationState } = useAuth();
  const [isConnecting, setIsConnecting] = useState(false);
  const [hasProcessedMessage, setHasProcessedMessage] = useState(false);

  // Wrap the callback functions to avoid recreating them
  const handleOAuthError = useCallback(
    (error: string, provider: AuthProvider) => {
      onError(error, provider);
    },
    [onError]
  );

  const handleSuccess = useCallback(
    (message: string, provider: AuthProvider) => {
      onSuccess(message, provider);
    },
    [onSuccess]
  );

  const handleClearError = useCallback(() => {
    onClearError();
  }, [onClearError]);

  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (hasProcessedMessage) return;

      const { success, state, code, provider, action } = event.data as OAuthSuccessResponse;

      if (action !== OAuthActionType.LINK) return;
      if (provider !== AuthProvider.GOOGLE) return;

      // Handle OAuth success for linking
      if (code && success) {
        setHasProcessedMessage(true);
        try {
          // Call the link endpoint with the OAuth code
          const response = await apiClient.post('/auth/oauth/google/link', {
            code,
            state,
          });

          // In the handleMessage function
          if (response.ok) {
            handleSuccess('Google account connected successfully', AuthProvider.GOOGLE);
            setIsConnecting(false);
            await refreshUserOrganisationState();
          } else {
            const errorData = await response.json();
            setIsConnecting(false);

            handleOAuthError(
              errorData.message || 'Failed to link Google account. Please try again.',
              AuthProvider.GOOGLE
            );
          }
        } catch (error) {
          console.error('Failed to link Google account:', error);
          handleOAuthError('Failed to link Google account. Please try again.', AuthProvider.GOOGLE);
          setIsConnecting(false);
        }
      }

      // Handle OAuth error
      else {
        setHasProcessedMessage(true);
        console.error('Google OAuth error:', event.data.message);
        handleOAuthError(
          event.data.message ||
            event.data.error ||
            'Failed to connect Google account. Please try again.',
          AuthProvider.GOOGLE
        );
        setIsConnecting(false);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [
    handleOAuthError,
    handleClearError,
    handleSuccess,
    hasProcessedMessage,
    refreshUserOrganisationState,
  ]); // Stable dependencies

  const handleConnect = () => {
    setIsConnecting(true);
    setHasProcessedMessage(false); // Reset the processed message state
    handleClearError();

    const width = 500;
    const height = 600;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const popup = window.open(
      `${process.env.REACT_APP_API_URL}/auth/oauth/google?popup=true&action=${OAuthActionType.LINK}&returnUrl=/security`,
      'google-oauth',
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`
    );

    if (!popup || popup.closed) {
      handleOAuthError(
        'Please allow popups for this site to connect your Google account.',
        AuthProvider.GOOGLE
      );
      setIsConnecting(false);
      return;
    }

    const checkPopup = setInterval(() => {
      if (popup.closed) {
        clearInterval(checkPopup);
        setTimeout(() => {
          if (isConnecting && !hasProcessedMessage) {
            handleOAuthError(
              'Connection cancelled or failed. Please try again.',
              AuthProvider.GOOGLE
            );
            setIsConnecting(false);
          }
        }, 500);
      }
    }, 1000);
  };

  const handleDisconnect = async () => {
    try {
      const response = await apiClient.post('/auth/oauth/google/disconnect');

      if (response.ok) {
        await refreshUserOrganisationState();
      } else {
        const errorData = await response.json();
        const errorMessage =
          errorData?.message || 'Failed to disconnect Google account. Please try again.';
        handleOAuthError(errorMessage, AuthProvider.GOOGLE);
      }
    } catch (error) {
      console.error('Error disconnecting Google account:', error);
      handleOAuthError(
        'Failed to disconnect Google account. Please try again.',
        AuthProvider.GOOGLE
      );
    }
  };

  return (
    <ProviderCard
      email={user?.email}
      provider={AuthProvider.GOOGLE}
      isConnected={user?.providers.includes(AuthProvider.GOOGLE) || false}
      isConnecting={isConnecting}
      onConnect={handleConnect}
      onDisconnect={handleDisconnect}
    />
  );
};

export default GoogleAccountLink;
